import React, { useState } from 'react';
import { X, Users, Bell, Clock } from 'lucide-react';

interface EventFormProps {
  onSubmit: (event: {
    title: string;
    description: string;
    startTime: Date;
    endTime: Date;
    allDay: boolean;
    location?: string;
    color?: string;
    reminders: Array<{ time: number; type: 'email' | 'push' | 'both' }>;
  }) => void;
  onClose: () => void;
}

export function EventForm({ onSubmit, onClose }: EventFormProps) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [startTime, setStartTime] = useState(() => {
    const date = new Date();
    date.setMinutes(Math.ceil(date.getMinutes() / 15) * 15);
    date.setSeconds(0);
    date.setMilliseconds(0);
    return date;
  });
  const [endTime, setEndTime] = useState(() => {
    const date = new Date();
    date.setMinutes(Math.ceil(date.getMinutes() / 15) * 15 + 30);
    date.setSeconds(0);
    date.setMilliseconds(0);
    return date;
  });
  const [allDay, setAllDay] = useState(false);
  const [location, setLocation] = useState('');
  const [color, setColor] = useState('#9FFF32');
  
  const formatDateTimeLocal = (date: Date) => {
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (endTime < startTime) {
      alert('End time must be after start time');
      return;
    }
    onSubmit({
      title,
      description,
      startTime,
      endTime,
      allDay,
      location,
      color,
      reminders: []
    });
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center">
      <div className="bg-[#2A2B2E] rounded-3xl p-6 max-w-md w-full mx-4">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-semibold">Add Event</h3>
          <button
            onClick={onClose}
            className="p-2 hover:bg-black/20 rounded-full"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Event title"
            className="w-full px-4 py-3 bg-black/20 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[var(--accent-color)]"
            required
          />

          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Description"
            className="w-full px-4 py-3 bg-black/20 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[var(--accent-color)] min-h-[80px] resize-none"
          />

          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={allDay}
                onChange={(e) => setAllDay(e.target.checked)}
                className="rounded bg-black/20"
              />
              <span className="text-sm">All day</span>
            </label>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-gray-400">Start</label>
              <div className="relative">
                <input
                  type={allDay ? 'date' : 'datetime-local'}
                  value={formatDateTimeLocal(startTime)}
                  onChange={(e) => setStartTime(new Date(e.target.value))}
                  className="w-full mt-1 pl-10 pr-4 py-2 bg-black/20 rounded-xl text-white"
                  required
                />
                <Clock className="absolute left-3 top-[60%] -translate-y-1/2 w-4 h-4 text-white" />
              </div>
            </div>
            <div>
              <label className="text-sm text-gray-400">End</label>
              <div className="relative">
                <input
                  type={allDay ? 'date' : 'datetime-local'}
                  value={formatDateTimeLocal(endTime)}
                  onChange={(e) => setEndTime(new Date(e.target.value))}
                  className="w-full mt-1 pl-10 pr-4 py-2 bg-black/20 rounded-xl text-white"
                  required
                />
                <Clock className="absolute left-3 top-[60%] -translate-y-1/2 w-4 h-4 text-white" />
              </div>
            </div>
          </div>

          <div>
            <label className="text-sm text-gray-400">Location</label>
            <input
              type="text"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              placeholder="Add location"
              className="w-full mt-1 px-4 py-2 bg-black/20 rounded-xl text-white"
            />
          </div>

          <div>
            <label className="text-sm text-gray-400">Color</label>
            <input
              type="color"
              value={color}
              onChange={(e) => setColor(e.target.value)}
              className="block w-full h-10 mt-1 rounded-xl"
            />
          </div>
          <button
            type="submit"
            className="w-full bg-[var(--accent-color)] text-black rounded-xl py-3 font-medium hover:opacity-90 transition-opacity"
          >
            Add Event
          </button>
        </form>
      </div>
    </div>
  );
}